import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography, Tooltip, Modal, Box, Button } from '@mui/material';

import { Unit, LeaseLifecycle, Process, ActionItem } from '@urbanmix-tech/shared-js';
import { usePortfolioOverview } from '@/shared/hooks/usePortfolioOverview';

import { ProcessIcon } from '@/components/shared/ProcessIcon';
import { ProjectCardList } from '@/components/ProjectCard';
import { Highlight } from '@/components/Highlights';
import { ProcessHighlight } from '@/components/Highlights/ProcessHighlight';
import { DateHighlight } from '@/components/Highlights/DateHighlight';
import { ProcessFilterBar } from '@/components/ProcessFilterBar';
import { Table, ITableCell, ITableRow } from '@/components/shared/Table';

import { useProjects } from '@/shared/hooks/useProjects';
import { useAuth } from '@/shared/context/AuthProvider';

import { NewProjectModalContent } from '@/components/NewProjectModalContent/NewProjectModalContent';
import { ErrorBoundary } from '@sentry/react';
import { GoogleConnectModalContent } from '@/components/GoogleConnectModalContent/GoogleConnectModalContent';
import { OAuthConnectSuccessModalContent } from '@/components/OAuthConnectSuccessModalContent/OAuthConnectSuccessModalContent';

function getNumber(num: number | null) {
  return num && !Number.isNaN(num) ? num : 0;
}

interface PortfolioPageProps {
  modal?: PortfolioModals | undefined;
}

export type PortfolioModals =
  | 'action'
  | 'project'
  | 'google_connect'
  | 'docusign_connect'
  | null;

const Portfolio = ({ modal }: PortfolioPageProps) => {
  const navigate = useNavigate();
  const projects = useProjects();
  const { userData, selectedOption, isEditMode } = useAuth();

  const [viewMode, setViewMode] = useState<'card-list' | 'table'>('card-list');
  const [statusFilter, setStatusFilters] = useState<LeaseLifecycle[]>([]);

  // Process filters
  const [processStatusFilter, setProcessStatusFilter] = useState<string[]>([]);
  const [processTypeFilter, setProcessTypeFilter] = useState<string[]>([]);
  const [unitProcessFilter, setUnitProcessFilter] = useState<string[]>([]);
  const [viewType, setViewType] = useState<'lease' | 'process' | 'date'>('process');

  // Date view filters
  const [datePeriodFilter, setDatePeriodFilter] = useState<string[]>([]);

  const [isModalOpen, setIsModalOpen] = useState<PortfolioModals>(null);

  // Single server-aggregated overview call replaces per-project fetches of all
  // units/processes/actions (and the live processes subscription). On-demand with
  // a manual refresh; the server caches per project and invalidates via triggers.
  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const { data: overview, loading, refresh } = usePortfolioOverview(projectIds);

  useEffect(() => {
    if (modal) {
      setIsModalOpen(modal);
    }
  }, [modal]);

  const unitsByProjectId = useMemo<Record<string, Unit[]>>(() => {
    const obj: Record<string, Unit[]> = {};
    overview?.projects.forEach((p) => {
      obj[p.projectId] = p.units as unknown as Unit[];
    });
    return obj;
  }, [overview]);

  const processesByProjectId = useMemo<Record<string, Process[]>>(() => {
    const obj: Record<string, Process[]> = {};
    overview?.projects.forEach((p) => {
      obj[p.projectId] = p.processes as unknown as Process[];
    });
    return obj;
  }, [overview]);

  // Reconstruct minimal action items from the compact task projection so the
  // existing card/highlight logic keeps working (dueDates are ISO strings, which
  // convertPseudoDateToRealDate handles).
  const actionItemsByProjectId = useMemo<Record<string, ActionItem[]>>(() => {
    const obj: Record<string, ActionItem[]> = {};
    overview?.projects.forEach((p) => {
      const unitBuilding: Record<string, number | null> = {};
      p.units.forEach((u) => {
        unitBuilding[u.id] = u.buildingId;
      });

      const scheduled = p.tasks.map((t, i) => ({
        id: `${p.projectId}:sch:${i}`,
        unitId: t.unitId,
        dueDate: t.dueDate,
        status: 'active',
        projectId: p.projectId,
        buildingId: unitBuilding[t.unitId] ?? undefined,
      }));

      const unscheduled = Array.from(
        { length: p.unscheduledTaskCount },
        (_, i) => ({
          id: `${p.projectId}:uns:${i}`,
          status: 'active',
          projectId: p.projectId,
        })
      );

      obj[p.projectId] = [...scheduled, ...unscheduled] as unknown as ActionItem[];
    });
    return obj;
  }, [overview]);

  const handleClickRow = (id: string) => {
    navigate(id);
  };

  const handleCloseModal = () => {
    setIsModalOpen(null);
  };

  const handleOpenProjectModal = () => {
    setIsModalOpen('project');
  };

  const filteredUnits = useMemo(() => {
    const obj: Record<string, Unit[]> = {};

    projects.forEach((project) => {
      if (!unitsByProjectId[project.id]) {
        obj[project.id] = [];
        return;
      }

      const tempUnits = unitsByProjectId[project.id].filter((unit) => {
        if (statusFilter.length === 0) {
          return true;
        }

        return Array.isArray(unit.currentState)
          ? unit.currentState.length > 0
            ? statusFilter.some((item) => unit.currentState.includes(item))
            : statusFilter.includes(LeaseLifecycle.NOTICE)
          : statusFilter.includes(
              (unit.currentState as LeaseLifecycle) || LeaseLifecycle.NOTICE
            );
      });

      obj[project.id] = tempUnits;
    });

    return obj;
  }, [projects, statusFilter, unitsByProjectId]);

  const rows: ITableRow[] = useMemo(() => {
    return projects.map((project) => {
      const units = filteredUnits[project.id];

      const affordableRent = units.reduce(
        (acc, unit) =>
          unit.affordable
            ? {
                sumOfAffordableRent:
                  acc.sumOfAffordableRent + getNumber(unit.actualRent),
                sumOfSQFT: acc.sumOfSQFT + getNumber(unit.sqft),
                units: acc.units + 1,
              }
            : { ...acc },
        { sumOfAffordableRent: 0, sumOfSQFT: 0, units: 0 }
      );
      const marketRent = units.reduce(
        (acc, unit) =>
          !unit.affordable
            ? {
                sumOfMarketRent:
                  acc.sumOfMarketRent + getNumber(unit.actualRent),
                sumOfSQFT: acc.sumOfSQFT + (unit.sqft || 0),
                units: acc.units + 1,
              }
            : { ...acc },
        { sumOfMarketRent: 0, sumOfSQFT: 0, units: 0 }
      );

      return {
        id: `/projects/${project.id}/${project.buildings[0].id}`,
        data: {
          Projects: {
            label: project.name,
          },
          Buildings: {
            label: `${project.buildings.length}`,
          },
          'Affordable Rate Units': {
            label:
              affordableRent.sumOfSQFT > 0
                ? `$${(
                    affordableRent.sumOfAffordableRent /
                    affordableRent.sumOfSQFT
                  ).toFixed(2)}/sqft`
                : '-',
          },
          'Market Rate Units': {
            label:
              marketRent.sumOfSQFT > 0
                ? `$${(
                    marketRent.sumOfMarketRent / marketRent.sumOfSQFT
                  ).toFixed(2)}/sqft`
                : '-',
          },
          Floors: {
            label: `${project.buildings[0].floors || 0}`,
          },
          'Total units': {
            label: `${units.length}`,
          },
        },
      } as ITableRow;
    });
  }, [projects, filteredUnits]);

  const columns: ITableCell[] = [
    { label: 'Projects' },
    { label: 'Buildings' },
    {
      label: 'Affordable Rate Units',
      children: (
        <Stack direction="row" alignItems="center" gap="4px">
          Affordable Rate Units
          <Tooltip
            title="Avg. price per sqft of affordable units in the portfolio"
            slotProps={{ tooltip: { className: '!max-w-[500px]' } }}
            placement="top"
          >
            <div>
              <ProcessIcon name="Info" size={14} />
            </div>
          </Tooltip>
        </Stack>
      ),
    },
    {
      label: 'Market Rate Units',
      children: (
        <Stack direction="row" alignItems="center" gap="4px">
          Market Rate Units
          <Tooltip
            title="Avg. price per sqft of market units in the portfolio"
            placement="top"
            slotProps={{ tooltip: { className: '!max-w-[500px]' } }}
          >
            <div>
              <ProcessIcon name="Info" size={14} />
            </div>
          </Tooltip>
        </Stack>
      ),
    },
    { label: 'Floors' },
    { label: 'Total units' },
  ];

  const handleChangeStatusFilter = useCallback((selected: LeaseLifecycle[]) => {
    setStatusFilters(selected);
  }, []);

  const handleChangeProcessStatusFilter = useCallback((selected: string[]) => {
    setProcessStatusFilter(selected);
  }, []);

  const handleChangeProcessTypeFilter = useCallback((selected: string[]) => {
    setProcessTypeFilter(selected);
  }, []);

  const handleChangeUnitProcessFilter = useCallback((selected: string[]) => {
    setUnitProcessFilter(selected);
  }, []);

  const handleChangeDatePeriodFilter = useCallback((selected: string[]) => {
    setDatePeriodFilter(selected);
  }, []);

  // Aggregate all processes and action items across projects
  const allProcesses = useMemo(() => {
    return Object.values(processesByProjectId).flat();
  }, [processesByProjectId]);

  const allActionItems = useMemo(() => {
    return Object.values(actionItemsByProjectId).flat();
  }, [actionItemsByProjectId]);

  const allUnits = useMemo(() => {
    return Object.values(unitsByProjectId).flat();
  }, [unitsByProjectId]);

  return (
    <Stack gap="12px" className="w-full min-w-[470px] h-full overflow-hidden">
      {/* Fixed Header */}
      <Stack 
        className="w-full p-24 pb-12 border-b border-white/5 bg-[#111116] z-10"
        gap="12px"
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap="16px">
            <Typography variant="h4" className="text-stone-200">
              {selectedOption.value !== 'all'
                ? selectedOption.label
                : 'Portfolio Homepage'}
            </Typography>

          <Stack direction="row" alignItems="center" gap="8px">
            <Button
              variant={viewType === 'process' ? 'contained' : 'outlined'}
              onClick={() => setViewType('process')}
              size="small"
              className="rounded-lg"
              sx={
                viewType !== 'process'
                  ? {
                      color: '#e7e5e4 !important',
                      borderColor: 'rgba(255, 255, 255, 0.1) !important',
                      '&:hover': {
                        color: '#3b82f6 !important',
                        borderColor: '#3b82f6 !important',
                        backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                      },
                    }
                  : undefined
              }
            >
              Process View
            </Button>
            <Button
              variant={viewType === 'lease' ? 'contained' : 'outlined'}
              onClick={() => setViewType('lease')}
              size="small"
              className="rounded-lg"
              sx={
                viewType !== 'lease'
                  ? {
                      color: '#e7e5e4 !important',
                      borderColor: 'rgba(255, 255, 255, 0.1) !important',
                      '&:hover': {
                        color: '#3b82f6 !important',
                        borderColor: '#3b82f6 !important',
                        backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                      },
                    }
                  : undefined
              }
            >
              Lease View
            </Button>
            <Button
              variant={viewType === 'date' ? 'contained' : 'outlined'}
              onClick={() => setViewType('date')}
              size="small"
              className="rounded-lg"
              sx={
                viewType !== 'date'
                  ? {
                      color: '#e7e5e4 !important',
                      borderColor: 'rgba(255, 255, 255, 0.1) !important',
                      '&:hover': {
                        color: '#3b82f6 !important',
                        borderColor: '#3b82f6 !important',
                        backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                      },
                    }
                  : undefined
              }
            >
              Date View
            </Button>
          </Stack>
          </Stack>

          <Stack direction="row" gap="12px" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={() => refresh()}
              disabled={loading}
              className="rounded-lg"
              sx={{
                color: '#e7e5e4 !important',
                borderColor: 'rgba(255, 255, 255, 0.1) !important',
                '&:hover': {
                  color: '#3b82f6 !important',
                  borderColor: '#3b82f6 !important',
                  backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                },
              }}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Typography variant="body2Medium" className="text-stone-200/80">
              {`${projects.length} Projects`}
            </Typography>
            <Typography variant="body2Medium" className="text-stone-200/80">
              |
            </Typography>
            <Typography variant="body2Medium" className="text-stone-200/80">
              {`${Object.values(filteredUnits).reduce(
                (acc, item) => [...acc, ...item],
                []
              ).length} Units`}
            </Typography>
          </Stack>
        </Stack>

        {/* Edit Mode Buttons */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="24px"
        >
          <Stack direction="row" alignItems="center" gap="24px">
            {userData?.isDev && isEditMode && (
              <Button
                onClick={handleOpenProjectModal}
                variant="outlined"
                className="rounded-lg bg-blue-500 text-white border-blue-500 hover:bg-blue-600"
                type="submit"
                size="small"
              >
                + Add Building
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto px-24 pb-24">
        <Stack gap="24px">
          {/* Stats Section */}
          <div className="w-full">
            {viewType === 'process' ? (
              <ProcessHighlight
                units={allUnits}
                processes={allProcesses}
                actionItems={allActionItems}
                processStatusFilter={processStatusFilter}
                processTypeFilter={processTypeFilter}
                unitProcessFilter={unitProcessFilter}
                onChangeProcessStatusFilter={handleChangeProcessStatusFilter}
                onChangeProcessTypeFilter={handleChangeProcessTypeFilter}
                onChangeUnitProcessFilter={handleChangeUnitProcessFilter}
              />
            ) : viewType === 'date' ? (
              <DateHighlight
                units={allUnits}
                processes={allProcesses}
                actionItems={allActionItems}
                datePeriodFilter={datePeriodFilter}
                onChangeDatePeriodFilter={handleChangeDatePeriodFilter}
              />
            ) : (
              <Highlight
                units={Object.values(filteredUnits).reduce(
                  (acc, item) => [...acc, ...item],
                  []
                )}
                statusFilter={statusFilter}
                onChangeStatusFilter={handleChangeStatusFilter}
              />
            )}
          </div>

          {/* Projects Section */}
          <div className="w-full">
            {viewMode === 'table' ? (
              <Table rows={rows} columns={columns} onClickRow={handleClickRow} />
            ) : (
              <ErrorBoundary fallback={<p>Error Loading Projects</p>}>
                <div className="w-full">
                  <ProjectCardList
                    projects={projects}
                    unitsByProjectId={filteredUnits}
                    processesByProjectId={processesByProjectId}
                    actionItemsByProjectId={actionItemsByProjectId}
                    statusFilter={statusFilter}
                    processStatusFilter={processStatusFilter}
                    processTypeFilter={processTypeFilter}
                    unitProcessFilter={unitProcessFilter}
                    datePeriodFilter={datePeriodFilter}
                    viewType={viewType}
                  />
                </div>
              </ErrorBoundary>
            )}
          </div>
        </Stack>
      </div>

      <Modal open={!!isModalOpen} onClose={handleCloseModal}>
        <Box
          component="div"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[10px] border border-stone-200 border-opacity-[0.15] shadow outline-none"
        >
          {isModalOpen === 'project' && (
            <NewProjectModalContent onClose={handleCloseModal} />
          )}
          {isModalOpen === 'google_connect' && (
            <GoogleConnectModalContent onClose={handleCloseModal} />
          )}
          {isModalOpen === 'docusign_connect' && (
            <OAuthConnectSuccessModalContent
              serviceName="DocuSign"
              onClose={handleCloseModal}
            />
          )}
        </Box>
      </Modal>
    </Stack>
  );
};

export default Portfolio;
